/**
 * environment.ts — the World (paintable ground truth) and the Env (one run).
 *
 * Two layers:
 *
 *   - `World` — the static grid of true cell classes the USER paints. It is
 *     the ground truth. The agent never sees it directly; it sees noisy
 *     sensor readings, which the Belief turns into a belief map. Painting
 *     mid-run is exactly the "non-stationary world" case the online RL loop
 *     is for.
 *
 *   - `Env` — one run's transient state: the agent's pose, the visited set,
 *     the belief map, the step counter, the reward function, the done flag.
 *     `reset()` starts a run from a pose; `step(a)` advances one tick and
 *     returns (reward, done, event).
 *
 * Reward design (v5 prototype):
 *
 *   +1.0          entering a gold cell (gold consumed, removed from the World)
 *   -0.3          flat bump cost when a leap is blocked
 *   -0.05/extra   additional cost per intended-but-unwalked cell of a blocked
 *                 leap (a length-8 leap that bumps at cell 1 costs -0.3 -0.05·7)
 *   +0.15         entering a NEW free cell (coverage shaping — the dense reward
 *                 that makes online collection informative)
 *   -0.01         per cell walked (liveness penalty)
 *
 * The bump cost scales with intended length so committing to long leaps into
 * stone-heavy areas is properly painful — this was the fix that stopped the
 * agent from overfitting to length-8 perimeter walks.
 *
 * The leap action space:
 *   8 directions × 4 leap lengths {1, 2, 4, 8} = 32 actions.
 *   Action a → (dir = a % 8, length = LEAP_LENGTHS[a / 8]).
 */

import {
  Belief,
  FREE,
  GOLD,
  STONE,
  sampleReading,
  SENSOR_RADIUS,
} from './belief';
import {
  type VariantSpec,
  MOB_BASELINE,
  knightPaths,
  isKnightAction,
  knightMoveIdx,
  isQueenAction,
  queenDirIdx,
} from './variant';
import { queenTarget } from './density';

/** The 8 single-cell directions, indexed 0..7. (dr, dc) — row grows South,
 *  col grows East.
 *
 *  Cardinals first (E/W/S/N), then diagonals (NE/NW/SE/SW). Diagonals are
 *  essential for vein-following: a spiral arm is a curve, and following a
 *  curve with only cardinal moves requires zig-zagging; with diagonals the
 *  curve is smooth. */
export const DIRECTIONS: ReadonlyArray<readonly [number, number]> = [
  [0, 1], // 0 = E
  [0, -1], // 1 = W
  [1, 0], // 2 = S
  [-1, 0], // 3 = N
  [-1, 1], // 4 = NE
  [-1, -1], // 5 = NW
  [1, 1], // 6 = SE
  [1, -1], // 7 = SW
];
export const N_DIRS = 8;

/** Leap lengths the Q-net chooses between. The action space is the Cartesian
 *  product {8 directions} × {these lengths}, so N_ACTIONS = 8 × 4 = 32.
 *  A leap of length L in direction D walks L cells in direction D in ONE
 *  tick (sensing + rewarding each).
 *
 *  Lengths grow by powers of 2 — this gives the agent both fine control near
 *  gold (length 1) and the ability to commit to long traversals into
 *  unexplored area (length 8) without spending a tick per cell. */
export const LEAP_LENGTHS: ReadonlyArray<number> = [1, 2, 4, 8];

/** Total action count = directions × leap lengths. */
export const N_ACTIONS = N_DIRS * LEAP_LENGTHS.length; // 32

/** Decoded action: either a straight leap (dir + len) or a knight L-shape. */
export interface DecodedAction {
  /** 'leap' for the 32 straight leaps; 'knight' for the 8 L-shapes;
   *  'queen' for the 8 directional density-targeted jumps. */
  kind: 'leap' | 'knight' | 'queen';
  /** Direction index 0..7 (leap and queen; -1 for knight). */
  dir: number;
  /** Leap length 1/2/4/8 (leap only; 3 for knight — the 3-cell L-path;
   *  -1 for queen — length determined by target distance at run time). */
  len: number;
  /** Knight move index 0..7 into KNIGHT_MOVES (knight only; -1 otherwise). */
  knightIdx: number;
  /** Queen direction index 0..7 into DIRECTIONS (queen only; -1 otherwise). */
  queenDir: number;
}

/**
 * Decode an action id, dispatching on the variant. Knight actions (index
 * >= spec.nLeapActions when useKnight) return `kind: 'knight'` with the
 * L-shape index; queen actions (index >= spec.nLeapActions when useQueen)
 * return `kind: 'queen'` with the direction index; straight leaps return
 * `kind: 'leap'` as before. The default spec is the baseline (32 leap-only
 * actions) so existing call sites keep working.
 */
export function decodeAction(a: number, spec: VariantSpec = MOB_BASELINE): DecodedAction {
  if (isKnightAction(a, spec)) {
    return { kind: 'knight', dir: -1, len: 3, knightIdx: knightMoveIdx(a, spec), queenDir: -1 };
  }
  if (isQueenAction(a, spec)) {
    const qd = queenDirIdx(a, spec);
    return { kind: 'queen', dir: qd, len: -1, knightIdx: -1, queenDir: qd };
  }
  const dir = a % N_DIRS;
  const len = LEAP_LENGTHS[(a / N_DIRS) | 0];
  return { kind: 'leap', dir, len, knightIdx: -1, queenDir: -1 };
}

/** Encode (direction index, leap-length-index) back into an action id. */
export function encodeAction(dir: number, lenIdx: number): number {
  return lenIdx * N_DIRS + dir;
}

/** Backwards-compat alias — legacy code (canMove, renderer) reads ACTIONS. */
export const ACTIONS = DIRECTIONS;

/** True if direction `d` is a diagonal move (both row and col change). */
export function isDiagonal(d: number): boolean {
  const [dr, dc] = DIRECTIONS[d];
  return dr !== 0 && dc !== 0;
}

/**
 * Is the move from (r, c) by direction `dir` legal on the given truth grid?
 * Blocks out-of-bounds, stones at the destination, AND corner-cutting on
 * diagonals: a diagonal move is blocked if EITHER of the two orthogonal
 * cells it "cuts between" is a stone.
 *
 * `stoneAt(r, c)` is a callback so this works for both the World's true
 * stones (env.step) and the belief's believed stones (feature computation).
 */
export function canMove(
  r: number,
  c: number,
  dir: number,
  inBounds: (r: number, c: number) => boolean,
  stoneAt: (r: number, c: number) => boolean,
): boolean {
  const [dr, dc] = DIRECTIONS[dir];
  const nr = r + dr;
  const nc = c + dc;
  if (!inBounds(nr, nc)) return false;
  if (stoneAt(nr, nc)) return false;
  if (dr !== 0 && dc !== 0) {
    // Diagonal: block if EITHER orthogonal neighbour is stone.
    if (stoneAt(r + dr, c) || stoneAt(r, c + dc)) return false;
  }
  return true;
}

export interface RewardWeights {
  gold: number;           // +1.0
  bumpBase: number;       // -0.3
  bumpPerUnwalked: number;// -0.05
  coverage: number;       // +0.15
  step: number;           // -0.01
}

export const DEFAULT_REWARDS: RewardWeights = {
  gold: 1.0,
  bumpBase: -0.3,
  bumpPerUnwalked: -0.05,
  coverage: 0.15,
  step: -0.01,
};

/**
 * The paintable ground-truth grid. Cell values are FREE / GOLD / STONE.
 * The UI paints on this; the Env senses against it; rendering reads it.
 *
 * `goldCount` is maintained incrementally so the done-check (`goldLeft <= 0`)
 * is O(1) even when the user paints thousands of cells mid-run.
 */
export class World {
  readonly grid: number;
  readonly cells: Uint8Array;
  private goldCount = 0;

  constructor(grid: number) {
    this.grid = grid;
    this.cells = new Uint8Array(grid * grid); // all FREE
  }

  /** Paint a cell. Respects mutual exclusion: painting GOLD or STONE
   *  overwrites whatever was there; painting FREE erases. Keeps goldCount
   *  consistent so the Env's done check stays correct under live edits. */
  paint(r: number, c: number, kind: number): void {
    if (!this.inBounds(r, c)) return;
    const i = r * this.grid + c;
    const prev = this.cells[i];
    if (prev === GOLD && kind !== GOLD) this.goldCount--;
    if (kind === GOLD && prev !== GOLD) this.goldCount++;
    this.cells[i] = kind;
  }

  /** Bulk-set from a generator (spiral, random, clear). Resets goldCount. */
  loadFrom(cells: ArrayLike<number>): void {
    const n = this.cells.length;
    for (let i = 0; i < n; i++) this.cells[i] = cells[i] ?? 0;
    this.goldCount = 0;
    for (let i = 0; i < n; i++) {
      if (this.cells[i] === GOLD) this.goldCount++;
    }
  }

  inBounds(r: number, c: number): boolean {
    return r >= 0 && r < this.grid && c >= 0 && c < this.grid;
  }

  get totalGold(): number {
    return this.goldCount;
  }

  get kindArray(): Uint8Array {
    return this.cells;
  }
}

export interface StepOutcome {
  reward: number;
  done: boolean;
  /** Human-readable event for the HUD: 'gold' | 'bump' | 'new' | 'step'. */
  event: 'gold' | 'bump' | 'new' | 'step';
  /** The new pose after the step. */
  r: number;
  c: number;
  /** True if this step was a queen meta-action that actually fired (walked
   *  to a density-target cell). False for bumps, no-ops, leaps, and knight
   *  actions. Consumed by the renderer to draw a blue halo. */
  queenFired: boolean;
}

/**
 * One episode's run state. Cheap to construct; `reset()` is what starts a run.
 * The agent's pose, the visited set, and the belief map all live here.
 */
export class Env {
  readonly belief: Belief;
  r = 0;
  c = 0;
  steps = 0;
  goldLeft = 0;
  done = false;
  /** Sensor reliability P(reading == truth). Mutable so the UI can tune it
   *  live; applied on the next sensing pass. */
  sensorP: number;
  /** Per-episode step cap (see constructor doc). Mutable for tuning. */
  maxSteps: number;
  /** Reward weights. Mutable so the UI can tune them live — the agent's
   *  tendency to grab gold vs explore vs avoid bumps is sensitive to these,
   *  and the demo exposes them as sliders for live debugging. The object is
   *  shared by reference, so mutate its fields, don't replace it. */
  rewards: RewardWeights;
  /** Visited set as a bit matrix — fast O(1) check for the coverage bonus. */
  private visited: Uint8Array;
  /** Reusable density-map buffer for `stepQueen`. Lazy-allocated on first
   *  queen step; sized to grid × grid. Null on baseline/knight variants. */
  private _densityBuf: Float64Array | null = null;

  constructor(
    readonly world: World,
    readonly rng: () => number,
    sensorP: number = 0.85,
    readonly beliefLr: number = 0.5,
    rewards: RewardWeights = DEFAULT_REWARDS,
    /** Per-episode step cap. When the agent hits this without clearing the
     *  gold the episode ends (env.done = true) and the orchestrator starts
     *  the next one. */
    maxSteps: number = 400,
    readonly priorBar: number = 0.1,
  ) {
    this.sensorP = sensorP;
    this.maxSteps = maxSteps;
    this.rewards = rewards;
    this.belief = new Belief(world.grid);
    this.visited = new Uint8Array(world.grid * world.grid);
  }

  /**
   * Start a run from pose (r, c). Builds the prior belief map (gold fraction
   * as the cold prior; a small stone prior), clears the start cell (the
   * agent always knows where it is), senses the neighbourhood so the first
   * observation is informative.
   */
  reset(r: number, c: number): void {
    const g = this.world.grid;
    this.r = r;
    this.c = c;
    this.steps = 0;
    this.goldLeft = this.world.totalGold;
    this.done = false;
    this.visited.fill(0);
    const total = g * g;
    const priorGold = total > 0 ? this.goldLeft / total : 0;
    this.belief.reset(priorGold, this.priorBar, r, c);
    this.visited[r * g + c] = 1;
    this.senseNeighbourhood();
  }

  /** Sense all cells in the (2·SENSOR_RADIUS + 1)² neighbourhood around the
   *  current pose. SENSOR_RADIUS = 3 → the agent sees a 7×7 window. */
  private senseNeighbourhood(): void {
    const g = this.world.grid;
    for (let dr = -SENSOR_RADIUS; dr <= SENSOR_RADIUS; dr++) {
      for (let dc = -SENSOR_RADIUS; dc <= SENSOR_RADIUS; dc++) {
        const rr = this.r + dr;
        const cc = this.c + dc;
        if (!this.world.inBounds(rr, cc)) continue;
        if (rr === this.r && cc === this.c) continue;
        const truth = this.world.cells[rr * g + cc];
        const reading = sampleReading(truth, this.sensorP, this.rng);
        this.belief.applyReading(rr, cc, reading, this.beliefLr);
      }
    }
  }

  /**
   * Execute one action (leap or knight). Decodes `action` per `spec` and
   * walks the resulting path in ONE tick, sensing + rewarding each traversed
   * cell. Stops early on a stone/wall/corner-cut.
   *
   * Knight actions have TWO candidate L-paths (long-leg-first or short-leg-
   * first); the env walks whichever is clear (path A preferred if both are).
   * If neither is clear, the action is a bump.
   *
   * **Bump-loop fix (v5):** every action consumes AT LEAST one step, even if
   * it bumps immediately. Without this, a cornered agent could rack up bumps
   * without advancing `steps`, and the episode cap would never trigger — the
   * episode would hang. The Python prototype had this bug; the TS port keeps
   * the fix.
   *
   * Reward accounting is HONEST: each traversed cell earns its own step
   * penalty + coverage/gold bonus. A length-8 leap into unexplored space
   * earns ~8 coverage bonuses (cheap); a length-8 leap through explored
   * space pays ~8 step penalties (expensive). When a leap bumps after k
   * cells, the (length − k) unwalked cells each cost `bumpPerUnwalked`.
   *
   * Decay + sensing happen ONCE per action (at the final pose).
   *
   * `spec` defaults to the baseline (32 leap-only actions) so existing
   * callers that don't pass it keep working.
   */
  step(action: number, spec: VariantSpec = MOB_BASELINE): StepOutcome {
    if (this.done) {
      return { reward: 0, done: true, event: 'step', r: this.r, c: this.c, queenFired: false };
    }
    const dec = decodeAction(action, spec);
    // Bump-loop fix: every action consumes ≥1 step regardless of outcome.
    this.steps++;
    if (dec.kind === 'knight') {
      return this.stepKnight(dec.knightIdx);
    }
    if (dec.kind === 'queen') {
      return this.stepQueen(dec.queenDir);
    }
    return this.stepLeap(dec.dir, dec.len);
  }

  /** Walk a straight leap — see `step()` for the contract. */
  private stepLeap(dir: number, len: number): StepOutcome {
    const rw = this.rewards;
    const g = this.world.grid;
    const inBounds = (rr: number, cc: number) => this.world.inBounds(rr, cc);
    const stoneAt = (rr: number, cc: number) => this.world.cells[rr * g + cc] === STONE;

    let totalReward = 0;
    let event: StepOutcome['event'] = 'step';
    let walked = 0;

    for (let i = 0; i < len; i++) {
      if (!canMove(this.r, this.c, dir, inBounds, stoneAt)) {
        totalReward += rw.bumpBase;
        totalReward += rw.bumpPerUnwalked * (len - walked);
        if (walked === 0) event = 'bump';
        break;
      }
      const [dr, dc] = DIRECTIONS[dir];
      const nr = this.r + dr;
      const nc = this.c + dc;
      const idx = nr * g + nc;
      totalReward += rw.step;
      walked++;
      if (this.world.cells[idx] === GOLD) {
        totalReward += rw.gold;
        event = 'gold';
        this.world.paint(nr, nc, FREE);
        this.goldLeft--;
      } else if (!this.visited[idx]) {
        totalReward += rw.coverage;
        if (event !== 'gold') event = 'new';
      } else {
        if (event !== 'gold' && event !== 'new') event = 'step';
      }
      this.r = nr;
      this.c = nc;
      this.visited[idx] = 1;
      this.belief.clearCell(nr, nc);
      if (this.goldLeft <= 0 || this.steps >= this.maxSteps) {
        this.done = true;
        break;
      }
    }

    if (this.goldLeft <= 0 || this.steps >= this.maxSteps) this.done = true;

    this.belief.decayRecency();
    this.belief.bRecent[this.r * g + this.c] = 1;
    this.senseNeighbourhood();
    return { reward: totalReward, done: this.done, event, r: this.r, c: this.c, queenFired: false };
  }

  /** Walk a knight L-shape — tries both candidate paths, walks the first
   *  clear one. Path A is long-leg-first, path B is short-leg-first. */
  private stepKnight(moveIdx: number): StepOutcome {
    const rw = this.rewards;
    const g = this.world.grid;
    const startR = this.r;
    const startC = this.c;
    const [pathA, pathB] = knightPaths(startR, startC, moveIdx);
    const cellWalkable = (rr: number, cc: number) =>
      this.world.inBounds(rr, cc) && this.world.cells[rr * g + cc] !== STONE;
    const clearA = pathA.every(([rr, cc]) => cellWalkable(rr, cc));
    const clearB = pathB.every(([rr, cc]) => cellWalkable(rr, cc));

    if (!clearA && !clearB) {
      // Both paths blocked — bump. Pay the flat bump cost + the 3 unwalked cells.
      const totalReward = rw.bumpBase + rw.bumpPerUnwalked * 3;
      if (this.goldLeft <= 0 || this.steps >= this.maxSteps) this.done = true;
      this.belief.decayRecency();
      this.belief.bRecent[this.r * g + this.c] = 1;
      this.senseNeighbourhood();
      return { reward: totalReward, done: this.done, event: 'bump', r: this.r, c: this.c, queenFired: false };
    }

    const path = clearA ? pathA : pathB;
    let totalReward = 0;
    let event: StepOutcome['event'] = 'step';
    for (const [nr, nc] of path) {
      const idx = nr * g + nc;
      totalReward += rw.step;
      if (this.world.cells[idx] === GOLD) {
        totalReward += rw.gold;
        event = 'gold';
        this.world.paint(nr, nc, FREE);
        this.goldLeft--;
      } else if (!this.visited[idx]) {
        totalReward += rw.coverage;
        if (event !== 'gold') event = 'new';
      } else {
        if (event !== 'gold' && event !== 'new') event = 'step';
      }
      this.r = nr;
      this.c = nc;
      this.visited[idx] = 1;
      this.belief.clearCell(nr, nc);
      if (this.goldLeft <= 0 || this.steps >= this.maxSteps) {
        this.done = true;
        break;
      }
    }

    if (this.goldLeft <= 0 || this.steps >= this.maxSteps) this.done = true;

    this.belief.decayRecency();
    this.belief.bRecent[this.r * g + this.c] = 1;
    this.senseNeighbourhood();
    return { reward: totalReward, done: this.done, event, r: this.r, c: this.c, queenFired: false };
  }

  /** Walk a queen jump toward direction `dirIdx` — the target is the
   *  argmax-density cell along that direction (computed from the belief).
   *  The queen walks one cell at a time along the dominant axis (both
   *  axes for diagonals), applying the standard per-cell reward. Stops
   *  on stone, out-of-bounds, gold empty, or step cap.
   *
   *  If no valid target exists in that direction (empty density cone or
   *  all paths blocked), pay the flat bump cost. This is the same contract
   *  as stepLeap's blocked-at-cell-0 case.
   *
   *  The walk happens on the TRUE world cells — the queen commits to a
   *  physical traversal, not a teleport. Per-cell rewards (gold pickup,
   *  coverage shaping, step penalty) are identical to stepLeap. */
  private stepQueen(dirIdx: number): StepOutcome {
    const rw = this.rewards;
    const g = this.world.grid;
    const startR = this.r;
    const startC = this.c;
    // Lazy-allocate the density buffer (one per env, reused across calls).
    if (this._densityBuf === null || this._densityBuf.length !== g * g) {
      this._densityBuf = new Float64Array(g * g);
    }
    const tgt = queenTarget(this, dirIdx, this._densityBuf);

    if (tgt === null) {
      // No valid target in this direction — pay the flat bump cost.
      const totalReward = rw.bumpBase + rw.bumpPerUnwalked * 1;
      if (this.goldLeft <= 0 || this.steps >= this.maxSteps) this.done = true;
      this.belief.decayRecency();
      this.belief.bRecent[this.r * g + this.c] = 1;
      this.senseNeighbourhood();
      return { reward: totalReward, done: this.done, event: 'bump', r: this.r, c: this.c, queenFired: false };
    }

    const [tr, tc] = tgt;
    let totalReward = 0;
    let event: StepOutcome['event'] = 'step';
    let walked = 0;

    // Walk one cell at a time toward the target. Stop on stone, OOB,
    // gold empty, or step cap. Diagonal moves step both axes together.
    // Per-cell rewards accumulate (step penalty, coverage, gold) but
    // `this.steps` is NOT incremented per cell — the dispatcher already
    // bumped it once for the whole action. This matches stepLeap's
    // contract: a length-8 leap walks 8 cells but consumes 1 planner step.
    while ((this.r !== tr || this.c !== tc) && !this.done) {
      const dr = tr === this.r ? 0 : (tr > this.r ? 1 : -1);
      const dc = tc === this.c ? 0 : (tc > this.c ? 1 : -1);
      const nr = this.r + dr;
      const nc = this.c + dc;
      if (nr < 0 || nr >= g || nc < 0 || nc >= g) break;
      if (this.world.cells[nr * g + nc] === STONE) {
        // Blocked mid-walk — keep what we walked, pay bump cost on the rest.
        // Estimate remaining as Chebyshev distance to target (rough).
        const remaining = Math.max(Math.abs(tr - this.r), Math.abs(tc - this.c));
        totalReward += rw.bumpBase + rw.bumpPerUnwalked * remaining;
        if (walked === 0) event = 'bump';
        break;
      }
      walked++;
      const idx = nr * g + nc;
      totalReward += rw.step;
      if (this.world.cells[idx] === GOLD) {
        totalReward += rw.gold;
        event = 'gold';
        this.world.paint(nr, nc, FREE);
        this.goldLeft--;
      } else if (!this.visited[idx]) {
        totalReward += rw.coverage;
        if (event !== 'gold') event = 'new';
      } else {
        if (event !== 'gold' && event !== 'new') event = 'step';
      }
      this.r = nr;
      this.c = nc;
      this.visited[idx] = 1;
      this.belief.clearCell(nr, nc);
      if (this.goldLeft <= 0) {
        this.done = true;
      }
    }

    if (this.goldLeft <= 0 || this.steps >= this.maxSteps) this.done = true;

    this.belief.decayRecency();
    this.belief.bRecent[this.r * g + this.c] = 1;
    this.senseNeighbourhood();
    // Suppress the unused-variable warning for startR/startC — they are
    // documented for debug-rendering; the renderer reads env.r/env.c instead.
    void startR; void startC;
    return { reward: totalReward, done: this.done, event, r: this.r, c: this.c, queenFired: true };
  }

  /** Visited matrix for the renderer (the "explored area" overlay). */
  get visitedCells(): Uint8Array {
    return this.visited;
  }

  /**
   * Re-sync `goldLeft` from the World's current gold count. Call this after
   * the user paints gold mid-run so the done-check (`goldLeft <= 0`) and the
   * HUD's "gold left" counter stay honest.
   */
  resyncGoldLeft(): void {
    this.goldLeft = this.world.totalGold;
    if (this.goldLeft <= 0 && this.steps > 0) {
      this.done = true;
    } else if (this.goldLeft > 0) {
      this.done = false;
    }
  }
}
